/**
 * GitHub App repo picker.
 *
 * Renders above a free-text repo-URL input. When the server has a GitHub App
 * configured, shows the repositories the user's installations can read
 * (read-only, chosen by the user at install time on GitHub) plus a link to
 * install/adjust the app. Selecting a repo fills the URL via onPick. When no
 * app is configured (e.g. desktop/local), renders nothing — the plain URL
 * input keeps working as before.
 */
import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { rememberGithubReturnPath } from './returnPath';

interface GithubAppInfo {
  configured: boolean;
  slug?: string;
  installUrl?: string;
}

interface RepoEntry {
  fullName: string;
  private: boolean;
  htmlUrl: string;
}

// Module-level cache so multiple picker instances (one per source row)
// don't refetch. Reset on page reload — good enough for this UI.
let cachedInfo: GithubAppInfo | null = null;
let cachedRepos: RepoEntry[] | null = null;

export function GithubRepoPicker({ onPick }: { onPick: (url: string) => void }) {
  const [info, setInfo] = useState<GithubAppInfo | null>(cachedInfo);
  const [repos, setRepos] = useState<RepoEntry[] | null>(cachedRepos);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const appInfo = cachedInfo ?? (await api.getGithubAppInfo());
        cachedInfo = appInfo;
        if (cancelled) return;
        setInfo(appInfo);
        if (!appInfo.configured) return;
        setLoading(true);
        const result = cachedRepos ? { repos: cachedRepos } : await api.listGithubRepos();
        cachedRepos = result.repos;
        if (!cancelled) setRepos(result.repos);
      } catch {
        // Not signed in / endpoint unavailable — silently fall back to URL input.
        if (!cancelled) setInfo({ configured: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!info?.configured) return null;

  return (
    <div className="flex items-center gap-2 mb-1">
      <select
        defaultValue=""
        onChange={(e) => {
          if (e.target.value) onPick(e.target.value);
        }}
        className="flex-1 px-2 py-1 border border-input rounded text-sm bg-background text-foreground"
      >
        <option value="" disabled>
          {loading
            ? 'Loading connected repositories…'
            : repos && repos.length > 0
              ? `Choose a connected repository (${repos.length})`
              : 'No repositories connected yet'}
        </option>
        {(repos ?? []).map((r) => (
          <option key={r.fullName} value={r.htmlUrl}>
            {r.fullName}{r.private ? ' (private)' : ''}
          </option>
        ))}
      </select>
      <a
        href={info.installUrl}
        target="_blank"
        rel="noreferrer"
        onClick={() => rememberGithubReturnPath()}
        className="text-xs whitespace-nowrap text-primary hover:underline"
        title="Choose which repositories veodiagram can read (read-only) on GitHub"
      >
        Connect repos →
      </a>
    </div>
  );
}

/** Test/setup hook: drop the module cache so a fresh sync is re-fetched. */
export function _invalidateGithubRepoCache() {
  cachedInfo = null;
  cachedRepos = null;
}
